import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { PageHeader } from '@ems/ui/patterns/page-header'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { ServiceOrdersSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import { serviceCategoriesQuery, servicePolicyQuery } from '#admin/features/exhibitor/queries.ts'
import { ServiceCategoriesTable } from '#admin/features/exhibitor/service-categories-table.tsx'
import { ServiceTermsPanel } from '#admin/features/exhibitor/service-terms.tsx'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/exhibition/service-terms` — what a service order costs beyond its services: when on-site
 * prices start, the surcharge, and the service categories with their installation fees.
 */
export const Route = createFileRoute('/_authenticated/exhibition/service-terms')({
  // ponytail: the service orders skeleton has the same shape (a header, a panel, a table).
  pendingComponent: ServiceOrdersSkeleton,
  staticData: { crumb: { parents: [{ label: 'Exhibition' }], label: 'Service terms' } },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(servicePolicyQuery()),
      context.queryClient.ensureQueryData(serviceCategoriesQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: ServiceTermsPage,
})

function ServiceTermsPage() {
  const { data: policy } = useSuspenseQuery(servicePolicyQuery())
  const { data: categories } = useSuspenseQuery(serviceCategoriesQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'exhibitor.manage')

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Service terms"
        description="These terms price every service order. A submitted order keeps the price it was submitted at."
      />

      {canManage ? null : <ReadOnlyNotice area="the service terms" />}

      <ServiceTermsPanel
        policy={policy}
        zone={manifest?.tenant.timezone ?? 'UTC'}
        canManage={canManage}
      />

      <ServiceCategoriesTable
        categories={categories.items}
        currency={manifest?.tenant.currencyCode ?? 'IDR'}
        canManage={canManage}
      />
    </div>
  )
}
